import { useCallback, useEffect, useState } from "react";
import { Check, ImagePlus, Pencil, Plus, Trash2, Upload, X, Wand2, Loader } from "lucide-react";
import { api, apiError, resolveMediaUrl, PLACEHOLDER_IMAGE } from "@/api";
import { hindiError, hindiFeedback } from "@/lib/adminFeedback";
import { filterRank } from "@/lib/adminSearch";
import { QC_CHECKS, QC_GRADES } from "@/lib/qc";

const STORAGE_OPTIONS = ["32GB", "64GB", "128GB", "256GB", "512GB", "1TB"];
const COLOR_OPTIONS = [
  { name: "Black", hex: "#1a1a1a" }, { name: "White", hex: "#f5f5f5" },
  { name: "Blue", hex: "#1d4ed8" }, { name: "Red", hex: "#dc2626" },
  { name: "Gold", hex: "#d97706" }, { name: "Silver", hex: "#9ca3af" },
  { name: "Purple", hex: "#7c3aed" }, { name: "Green", hex: "#16a34a" },
  { name: "Pink", hex: "#ec4899" }, { name: "Orange", hex: "#ea580c" },
  { name: "Gray", hex: "#4b5563" }, { name: "Space Gray", hex: "#374151" },
];

const SALE_SECTIONS = [["flash-deals", "Flash Deals"], ["mobile-parts-deals", "Mobile Parts Deals"], ["today-deals", "Today's Deals"], ["deal-of-the-day", "Deal of the Day"], ["new-stock", "New Stock"], ["stock-clearance", "Stock Clearance"]];
const empty = { id: "", name: "", slug: "", sub: "", description: "", category_slug: "mobiles", price: "", original_price: "", stock: "", tag: "NEW", active: true, images: [], imageUrl: "", qc_grade: "new", qc_status: {}, imei_number: "", barcode: "", warranty_days: 365, homepage_sections: ["flash-deals"], storage_options: [], color_options: [], variantPrices: {} };

export default function AdminProductManager({ query = "" }) {
  const [products, setProducts] = useState([]), [categories, setCategories] = useState([]), [form, setForm] = useState(empty), [busy, setBusy] = useState(false);
  const [removeBg, setRemoveBg] = useState(true); // auto BG removal on by default
  const [uploading, setUploading] = useState(false);
  const load = useCallback(async () => { try { const [p, c] = await Promise.all([api.get("/api/admin/products?page_size=100"), api.get("/api/admin/categories")]); setProducts(p.data.items); setCategories(c.data); } catch (e) { hindiError(apiError(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const set = (key, value) => setForm((row) => ({ ...row, [key]: value }));
  const setQc = (key, value) => setForm((row) => ({ ...row, qc_status: { ...row.qc_status, [key]: value } }));

  const toggleStorage = (s) => set("storage_options", form.storage_options.includes(s) ? form.storage_options.filter((x) => x !== s) : [...form.storage_options, s]);
  const toggleColor = (c) => set("color_options", form.color_options.some((x) => x.name === c.name) ? form.color_options.filter((x) => x.name !== c.name) : [...form.color_options, c]);

  const save = async (event) => {
    event.preventDefault();
    if (!form.images.length) return hindiError("Add at least one product photo");
    setBusy(true);
    try {
      const variants = form.storage_options.length > 0
        ? form.storage_options.map((s) => ({
            sku: s.toLowerCase().replace(/\s+/g, "-"),
            name: s,
            attributes: { storage: s },
            price: Number(form.variantPrices?.[s]) || Number(form.price),
            stock: Number(form.stock),
          }))
        : [];
      const body = { ...form, price: Number(form.price), original_price: Number(form.original_price), stock: Number(form.stock), warranty_days: Number(form.warranty_days), variants };
      if (form.id) await api.patch(`/api/admin/products/${form.id}`, body);
      else await api.post("/api/admin/products", body);
      hindiFeedback("Product saved live");
      setForm(empty);
      load();
    } catch (e) { hindiError(apiError(e)); } finally { setBusy(false); }
  };

  const upload = async (event) => {
    const files = [...event.target.files]; if (!files.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map(async (file) => {
        const data = new FormData(); data.append("file", file);
        const url = `/api/admin/uploads${removeBg ? "?remove_bg=true" : ""}`;
        return (await api.post(url, data, { headers: { "Content-Type": "multipart/form-data" } })).data.url;
      }));
      set("images", [...form.images, ...urls]);
      if (removeBg) hindiFeedback("Background hat gayi! Clean product image ready hai");
    } catch (e) { hindiError(apiError(e)); } finally { setUploading(false); event.target.value = ""; }
  };

  const edit = (p) => setForm({ ...empty, ...p, price: String(p.price), original_price: String(p.original_price), stock: String(p.stock), warranty_days: Number(p.warranty_days || 0), qc_status: p.qc_status || {}, homepage_sections: p.homepage_sections?.length ? p.homepage_sections : ["flash-deals"], storage_options: p.storage_options || [], color_options: p.color_options || [], variantPrices: p.variants?.reduce((acc, v) => ({ ...acc, [v.name]: String(v.price) }), {}) || {} });
  const toggle = async (p) => { try { await api.patch(`/api/admin/products/${p.id}`, { ...p, active: !p.active }); load(); } catch (e) { hindiError(apiError(e)); } };
  const remove = async (p) => { if (!window.confirm(`Remove ${p.name}?`)) return; try { await api.delete(`/api/admin/products/${p.id}`); load(); } catch (e) { hindiError(apiError(e)); } };
  const visible = filterRank(products, query, [(p) => p.name, (p) => p.slug]);
  const toggleSection = (section) => set("homepage_sections", form.homepage_sections.includes(section) ? form.homepage_sections.filter((item) => item !== section) : [...form.homepage_sections, section]);

  return (
    <div className="admin-manager" data-testid="product-manager">
      <section className="admin-panel product-editor">
        <div className="panel-head">
          <div><h2>{form.id ? "Edit live product" : "Add product"}</h2><span>Category, homepage placement, warranty, QC and device IDs publish live.</span></div>
          {form.id && <button type="button" onClick={() => setForm(empty)}><X size={15} /> Cancel</button>}
        </div>
        <form className="product-form" onSubmit={save}>
          <input required value={form.name} onChange={(e) => { set("name", e.target.value); if (!form.id) set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")); }} placeholder="Product name" data-testid="product-name-input" />
          <input required value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="Product URL slug" data-testid="product-slug-input" />
          <input value={form.sub} onChange={(e) => set("sub", e.target.value)} placeholder="Short details" />
          <label className="product-category-select"><span>Live storefront category · {categories.length} available</span><select value={form.category_slug} onChange={(e) => set("category_slug", e.target.value)} data-testid="product-category-select">{categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}</select></label>
          <fieldset className="product-section-placement" data-testid="product-section-placement"><legend>Homepage sale placement</legend><small>Select where this product appears and where its View All link will list it.</small><div>{SALE_SECTIONS.map(([id, label]) => <label key={id}><input type="checkbox" checked={form.homepage_sections.includes(id)} onChange={() => toggleSection(id)} data-testid={`product-section-${id}`} /> {label}</label>)}</div></fieldset>
          <input required type="number" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="Live price (₹)" data-testid="product-price-input" />
          <input required type="number" min="0" value={form.original_price} onChange={(e) => set("original_price", e.target.value)} placeholder="Original price (₹)" />
          <input required type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} placeholder="Stock" data-testid="product-stock-input" />
          <input type="number" min="0" max="1095" value={form.warranty_days} onChange={(e) => set("warranty_days", e.target.value)} placeholder="Warranty days" data-testid="product-warranty-days-input" />
          <input value={form.tag} onChange={(e) => set("tag", e.target.value)} placeholder="Badge" />
          <input value={form.imei_number} onChange={(e) => set("imei_number", e.target.value)} placeholder="IMEI number (optional)" data-testid="product-imei-input" />
          <input value={form.barcode} onChange={(e) => set("barcode", e.target.value)} placeholder="Barcode (optional)" data-testid="product-barcode-input" />
          <textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Product description" />

          {/* Storage Options */}
          <div className="variant-options-section" data-testid="storage-options-section">
            <label>Storage Options (select all available)</label>
            <div className="storage-pills">
              {STORAGE_OPTIONS.map((s) => (
                <button type="button" key={s} className={`storage-pill${form.storage_options.includes(s) ? " active" : ""}`} onClick={() => toggleStorage(s)} data-testid={`storage-${s}`}>{s}</button>
              ))}
            </div>
          </div>

          {/* Per-Storage Variant Pricing */}
          {form.storage_options.length > 0 && (
            <div className="variant-pricing-section" data-testid="variant-pricing-section">
              <label><b>Price per Storage Variant</b> <small style={{ color: "rgba(255,255,255,0.4)" }}>optional — blank means default price</small></label>
              {form.storage_options.map((s) => (
                <div key={s} className="variant-price-row" data-testid={`variant-price-${s}`}>
                  <span className="variant-storage-label">{s}</span>
                  <input
                    type="number"
                    min="0"
                    value={form.variantPrices?.[s] || ""}
                    onChange={(e) => set("variantPrices", { ...form.variantPrices, [s]: e.target.value })}
                    placeholder={`Price for ${s} (₹) — default: ${form.price || "—"}`}
                    data-testid={`variant-price-input-${s.toLowerCase()}`}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Color Options */}
          <div className="variant-options-section" data-testid="color-options-section">
            <label>Color Options (select all available)</label>
            <div className="color-swatches">
              {COLOR_OPTIONS.map((c) => {
                const selected = form.color_options.some((x) => x.name === c.name);
                return (
                  <div key={c.name} title={c.name} className={`color-swatch${selected ? " active" : ""}`} style={{ backgroundColor: c.hex }} onClick={() => toggleColor(c)} data-testid={`color-${c.name.toLowerCase().replace(" ", "-")}`}>
                    <span className="swatch-check" style={{ color: ["White", "Silver", "Gold"].includes(c.name) ? "#333" : "#fff" }}>✓</span>
                  </div>
                );
              })}
            </div>
            {form.color_options.length > 0 && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>{form.color_options.map((c) => c.name).join(", ")}</div>}
          </div>

          {/* Image Upload with BG Remove */}
          <div className="product-media-controls">
            <label className={`bg-remove-toggle${removeBg ? " active" : ""}`}>
              <input type="checkbox" checked={removeBg} onChange={(e) => setRemoveBg(e.target.checked)} data-testid="bg-remove-toggle" />
              <Wand2 size={14} /> Auto Background Remove (AI)
            </label>
            <label className="upload-zone" data-testid="upload-image-label">
              {uploading ? <span className="upload-processing"><Loader size={14} className="spin" /> Processing{removeBg ? " + removing background…" : "…"}</span> : <><Upload size={16} /> Upload photos</>}
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={upload} disabled={uploading} />
            </label>
            <input value={form.imageUrl} onChange={(e) => set("imageUrl", e.target.value)} placeholder="Image URL" />
            <button type="button" onClick={() => /^https?:\/\//.test(form.imageUrl) && setForm((r) => ({ ...r, images: [...r.images, r.imageUrl], imageUrl: "" }))}><Plus size={14} /></button>
          </div>
          <div className="product-image-previews">
            {form.images.map((url, index) => (
              <div className="product-image-thumb" key={url}>
                <img src={resolveMediaUrl(url)} alt="Product" onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} />
                <button type="button" onClick={() => set("images", form.images.filter((_, i) => i !== index))}><X size={13} /></button>
              </div>
            ))}
            {!form.images.length && <span className="image-empty"><ImagePlus size={16} /> Add product photos</span>}
          </div>

          {/* QC */}
          <div className="qc-editor"><div className="qc-editor-head"><div><b>Quality Check</b><small>Select actual device condition.</small></div></div><div className="qc-grade-tabs">{QC_GRADES.map((grade) => <button type="button" className={form.qc_grade === grade.value ? "active" : ""} key={grade.value} onClick={() => set("qc_grade", grade.value)}>{grade.label}</button>)}</div><div className="qc-check-grid">{QC_CHECKS.map((check) => <div className="qc-check-item" key={check}><b>{check}</b><div className="qc-toggle">{[["pass", "✓"], ["fail", "!"], ["unknown", "—"]].map(([value, label]) => <button type="button" className={form.qc_status[check] === value ? `on ${value}` : ""} onClick={() => setQc(check, value)} key={value}>{label}</button>)}</div></div>)}</div></div>
          <label className="publish-check"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Publish on website</label>
          <button className="primary-btn" disabled={busy || uploading} data-testid="save-product-button"><Check size={15} /> {busy ? "Saving…" : form.id ? "Update product" : "Publish product"}</button>
        </form>
      </section>

      <section className="admin-panel table-panel admin-wide">
        <div className="panel-head"><div><h2>Live product catalog</h2><span>{visible.length} matching products</span></div></div>
        <div className="product-admin-grid">
          {visible.map((p) => (
            <article className="admin-product-card" key={p.id}>
              <img src={resolveMediaUrl(p.images?.[0]) || PLACEHOLDER_IMAGE} alt={p.name} onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} />
              <div>
                <span className={p.active ? "status-live" : "status-draft"}>{p.active ? "LIVE" : "DRAFT"}</span>
                <h3>{p.name}</h3>
                <p>{p.category_slug} · {p.warranty_days || 0}d warranty · {p.stock} stock</p>
                {(p.storage_options?.length > 0 || p.color_options?.length > 0) && (
                  <p style={{ fontSize: 11, color: "#00d4ff", marginTop: 2 }}>
                    {p.storage_options?.length > 0 && `💾 ${p.storage_options.join(", ")}`}
                    {p.storage_options?.length > 0 && p.color_options?.length > 0 && " · "}
                    {p.color_options?.length > 0 && `🎨 ${p.color_options.map((c) => c.name).join(", ")}`}
                  </p>
                )}
                <small className="product-placement-label">Homepage: {(p.homepage_sections || []).map((section) => SALE_SECTIONS.find(([id]) => id === section)?.[1]).filter(Boolean).join(" · ") || "Not placed"}</small>
              </div>
              <div className="admin-product-actions">
                <button onClick={() => edit(p)}><Pencil size={14} /> Edit</button>
                <button onClick={() => toggle(p)}>{p.active ? "Unpublish" : "Publish"}</button>
                <button onClick={() => remove(p)}><Trash2 size={14} /></button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}